import { Suspense, lazy, type ComponentProps } from 'react';
import { Skeleton } from '../Skeleton';
import type { RichTextField as RichTextFieldType } from './RichTextField';

/**
 * `<RichTextField>`, loaded on demand.
 *
 * **Use this, not the editor directly.** The editor and its document model are around 450 kB
 * of JavaScript — more than the entire rest of this application — and it appears in exactly
 * one modal. Imported statically it lands in the main bundle, so every sign-in pays for it
 * to render a login form.
 *
 * Splitting it here rather than at each call site means the next module that wants a
 * rich-text field gets the split for free, and there is one place that knows the editor is
 * heavy. The boundary is honest about the wait: the fallback is the field's own shape at its
 * own size, so the form does not reflow when the real editor arrives.
 */
const RichTextField = lazy(() =>
  import('./RichTextField').then((module) => ({ default: module.RichTextField })),
);

export function LazyRichTextField(props: ComponentProps<typeof RichTextFieldType>) {
  return (
    <Suspense fallback={<Placeholder label={props.label} required={props.required} />}>
      <RichTextField {...props} />
    </Suspense>
  );
}

/**
 * The field's silhouette while the editor loads: a real label, then a toolbar-and-document
 * shaped box at the height the editor settles at.
 */
function Placeholder({ label, required = false }: { label: string; required?: boolean }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex min-h-5 items-center gap-1.5">
        <span className="text-sm text-slate-700">
          {label}
          {required ? (
            <span className="text-red-600" aria-hidden="true">
              {' '}
              *
            </span>
          ) : null}
        </span>
      </div>

      <div
        // Announced rather than silent: the control the label points at is not there yet.
        role="status"
        aria-busy="true"
        aria-label={`${label} editor is loading`}
        className="overflow-hidden rounded-lg border border-slate-300 bg-white"
      >
        <div className="flex items-center gap-1.5 border-b border-slate-200 bg-slate-50 px-1.5 py-1">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-8 w-16" />
          ))}
          <Skeleton className="h-8 w-8" />
          <Skeleton className="h-8 w-8" />
        </div>

        <div className="flex flex-col gap-2 px-3 py-2">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      </div>
    </div>
  );
}
